'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useScrollMethods } from '../useScrollMethods'

export function useStickyElement(containerId?: string, offsetTop = 0) {
  const elementRef = useRef<HTMLDivElement | null>(null)
  const [isStuck, setIsStuck] = useState(false)
  const [elementTop, setElementTop] = useState<number | null>(null)

  const { scrollMethodsRef, scrollContainer } = useScrollMethods(containerId)

  // Set the initial top position of the element
  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    const updateElementTop = () => {
      const rect = element.getBoundingClientRect()
      requestAnimationFrame(() => setElementTop(rect.top - offsetTop))
    }

    updateElementTop()

    // Handle reflows by updating the elementTop on window resize
    window.addEventListener('resize', updateElementTop)

    // eslint-disable-next-line consistent-return
    return () => window.removeEventListener('resize', updateElementTop)
  }, [offsetTop])

  // Memoized handleScroll function
  const handleScroll = useCallback(() => {
    const element = elementRef.current
    const scrollMethods = scrollMethodsRef.current

    if (!element || !scrollMethods || elementTop === null) return

    const scrollTop = scrollMethods.getScrollPosition().y

    requestAnimationFrame(() => {
      if (scrollTop >= elementTop) {
        if (!isStuck) setIsStuck(true)
        element.style.transform = `translateY(${scrollTop - elementTop}px)`
      } else {
        if (isStuck) setIsStuck(false)
        element.style.transform = 'translateY(0px)'
      }
    })
  }, [elementTop, isStuck, scrollMethodsRef])

  // Attach and detach scroll event listener
  useEffect(() => {
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, {
        passive: true,
      })
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll)
      }
    }
  }, [scrollContainer, handleScroll])

  return { elementRef, isStuck }
}
